import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import type { Store } from "@/services/features/StoreService";
import {
  AnalyticsCardData,
  DateRange,
  storePerformanceService,
} from "@/services/StorePerformanceService";
import { formatCurrencyInStoreWithNoDecimals } from "@/utils/currency";
import { formatStoreDateRangeShort, getBrowserTimezone } from "@/utils/storeTime";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";

export const useStoreRevenue = (
  storeId: number | null,
  dateRange?: DateRange,
  latestSalesDate?: Date | null,
  _deviceType?: DeviceType,
  store?: Store | null,
  salesWindowReady = true,
  areaIds?: number[],
): AnalyticsCardData => {
  const areaIdsKey = areaIds?.join(",") ?? "";
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const [rawData, setRawData] = useState<{
    totalBasketValue: number;
    store: Store | null | undefined;
    startDate: string;
    endDate: string;
    isLimited: boolean;
  } | null>(null);
  const beginRequest = useLatestRequestGuard();

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!storeId || !dateRange || !salesWindowReady) {
      setRawData(null);
      return;
    }

    const fetchRevenue = async () => {
      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const salesWindow = storePerformanceService.getSalesQueryWindow(
          dateRange,
          latestSalesDate,
          browserTimezone,
        );

        if (!salesWindow.isAvailable) {
          if (isCurrent()) setRawData(null);
          return;
        }

        const response = await analyticsService.getSalesSummary({
          storeIds: [storeId],
          startDate: salesWindow.startDate,
          endDate: salesWindow.endDate,
          // Revenue is currently attributed exclusively to fitting room sessions.
          deviceType: "FITTING_ROOM",
          ...(areaIdsKey ? { areaIds: areaIdsKey.split(",").map(Number) } : {}),
        });

        const stats = response?.data;
        if (!stats) {
          if (isCurrent()) setRawData(null);
          return;
        }

        // If there is no revenue, return empty value as requested
        if ((stats.totalBasketValue ?? 0) === 0) {
          if (isCurrent()) setRawData(null);
          return;
        }

        if (!isCurrent()) return;
        setRawData({
          totalBasketValue: Number(stats.totalBasketValue || 0),
          store,
          startDate: salesWindow.startDate,
          endDate: salesWindow.endDate,
          isLimited: salesWindow.isLimited,
        });
      } catch (error) {
        console.error("Error fetching store revenue:", error);
        if (isCurrent()) setRawData(null);
      }
    };

    fetchRevenue();
    // Depend on primitive timestamps to avoid re-fetches caused by new object identities.
  }, [
    storeId,
    browserTimezone,
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
    latestSalesDate?.getTime(),
    salesWindowReady,
    store,
    areaIdsKey,
  ]);

  return useMemo(() => {
    if (!rawData) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: "",
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
      };
    }

    const salesWindow = formatStoreDateRangeShort(
      rawData.startDate,
      rawData.endDate,
      browserTimezone,
      currentLanguage,
      { weekday: "short", month: "short", day: "numeric" },
    );
    return {
      previousValue: null,
      value: formatCurrencyInStoreWithNoDecimals(rawData.totalBasketValue, rawData.store),
      subtitle: i18n.t("common.analytics.salesWindow", salesWindow),
      isPositive: true,
      pillText: "",
      rawValue: rawData.totalBasketValue,
      salesWindow: rawData,
    };
  }, [browserTimezone, rawData, currentLanguage]);
};
